import { CSSProperties, FC, useMemo } from "react";
import { Chart } from "../../../components/charts/Chart";
import { LineChart } from "../../../components/charts/LineChart";
import { getTeamColors } from "components/colors";
import { ensurePair, extractFirstNonZero } from "./helper";
import { Text } from "components/common/Text";

export const Stats: FC<{ status: any }> = ({ status }) => {
  const { home, away } = getTeamColors(status as any);
  const data = useMemo(() => {
    if (!status || typeof status !== "object") return {} as any;

    const splitStatus = String(status.sts || "")
      .split("|")
      .filter(Boolean);

    const pointsOnServe = extractFirstNonZero(splitStatus, [
      "Points Won on Serve",
      "POINTS WON ON SERVE",
      "Serve Points Won",
      "SERVE POINTS WON",
    ]);

    const longestStreak = extractFirstNonZero(splitStatus, [
      "Longest Streak",
      "LONGEST STREAK",
      "Current Streak",
      "CURRENT STREAK",
    ]);

    const sets: { key: string; v: [number, number] }[] = [];
    const c = (status as any).c || {};

    (["S1", "S2", "S3", "S4", "S5"] as const).forEach((k) => {
      const v = c?.[k];
      if (v) sets.push({ key: k, v: ensurePair(v) });
    });

    if (c?.T) sets.push({ key: "T", v: ensurePair(c.T) });
    const turn = c?.TURN ? ensurePair(c.TURN) : null;

    return {
      pointsOnServe,
      longestStreak,
      sets,
      turn,
    } as const;
  }, [status]);

  const cssVariables = useMemo(
    () =>
      ({
        "--home-color": home,
        "--away-color": away,
      } as CSSProperties),
    [home, away]
  );

  return (
    <div
      className="flex flex-col items-center justify-start gap-[2vw]"
      style={cssVariables}
    >
      <div className="flex w-full flex-row items-stretch gap-[5vw] justify-center">
        <Chart
          homeValue={data.pointsOnServe ? data.pointsOnServe[0] : 0}
          awayValue={data.pointsOnServe ? data.pointsOnServe[1] : 0}
          title="POINTS WON ON SERVE"
        />
        <Chart
          homeValue={data.longestStreak ? data.longestStreak[0] : 0}
          awayValue={data.longestStreak ? data.longestStreak[1] : 0}
          title="LONGEST STREAK"
        />
      </div>

      {data.sets && data.sets.length > 0 && (
        <div className="grid w-full gap-[1.5vw] [grid-template-columns:repeat(auto-fit,minmax(14vw,1fr))]">
          {data.sets.map(({ key, v }: { key: string; v: [number, number] }) => (
            <div
              key={key}
              className="flex flex-col gap-[1vw] rounded-[0.5vw] border-2 p-[1vw] bg-darkestBlue border-border"
            >
              <div className="font-bold opacity-90 text-statisticTitle">
                <Text variant="chartTitle">{key}</Text>
              </div>
              <div className="grid grid-cols-2 gap-[1vw]">
                <div
                  className="rounded-[0.5vw] border-b-[1vw] bg-white p-[1vw] text-center font-extrabold text-black"
                  style={{
                    borderBottomColor: "var(--home-color)",
                  }}
                >
                  <Text variant="chartValue">{v[0]}</Text>
                </div>
                <div
                  className="rounded-[0.5vw] border-b-[1vw] bg-white p-[1vw] text-center font-extrabold text-black"
                  style={{
                    borderBottomColor: "var(--away-color)",
                  }}
                >
                  <Text variant="chartValue">{v[1]}</Text>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex w-full flex-col gap-[2vw]">
        {data.pointsOnServe &&
          (data.pointsOnServe[0] !== 0 || data.pointsOnServe[1] !== 0) && (
            <LineChart
              homeValue={data.pointsOnServe[0]}
              awayValue={data.pointsOnServe[1]}
              title="SERVE POINTS (CUMULATIVE)"
            />
          )}
        {data.longestStreak &&
          (data.longestStreak[0] !== 0 || data.longestStreak[1] !== 0) && (
            <LineChart
              homeValue={data.longestStreak[0]}
              awayValue={data.longestStreak[1]}
              title="LONGEST STREAK (SET-ADJUSTED)"
            />
          )}
      </div>
    </div>
  );
};
